import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { decideApproval, myApprovalsQuery, type AwaitingApproval } from '../api/easeedeskApprovals'
import { LoadFailed } from '../ui/LoadFailed'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'

/**
 * Easeedesk → Awaiting my approval. Ecran nou, numai in v4.
 *
 * Tichetele care asteapta hotararea OMULUI LOGAT, pe un pas de flux cu
 * aprobarea "User manager": e pe cerere ca manager al solicitantului (din
 * IAM), ca sef al grupului (cand solicitantul n-are manager) sau, pentru un
 * administrator, pe o cerere care n-are pe nimeni.
 *
 * Nu cere alt drept si nu arata fisa tichetului - numai ce trebuie ca sa
 * hotarasca, ca pagina linkului din email. Serverul verifica la fiecare
 * hotarare ca omul e chiar cel numit pe cerere.
 */

const AS_LABEL: Record<AwaitingApproval['as'], string> = {
  manager: 'You are the manager of the requester',
  team_manager: 'You manage the assigned group',
  administrator: 'Nobody else can approve it',
}

function failed(error: unknown) {
  toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
}

function ApprovalCard({ item }: { item: AwaitingApproval }) {
  const queryClient = useQueryClient()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') => decideApproval(item.id, decision, reason),
    onSuccess: (answer) => {
      toast(answer.message ?? 'Done.', 'Approvals')
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'approvals'] })
    },
    onError: failed,
  })

  return (
    <div className="panel panel-default od-approval" data-approval={item.id}>
      <div className="panel-heading">
        <strong>
          #{item.ticket} {item.subject}
        </strong>
        {item.status !== '' && (
          <span
            className="label pull-right"
            style={{ backgroundColor: item.status_color !== '' ? item.status_color : '#8c97a1', color: '#fff' }}
          >
            {item.status}
          </span>
        )}
      </div>
      <div className="panel-body">
        <dl className="dl-horizontal" style={{ marginBottom: 10 }}>
          <dt>Requester</dt>
          <dd>{item.requester !== '' ? item.requester : '-'}</dd>
          {item.company !== '' && (
            <>
              <dt>Company</dt>
              <dd>{item.company}</dd>
            </>
          )}
          <dt>Requested</dt>
          <dd>{item.requested_at !== '' ? item.requested_at : '-'}</dd>
          <dt>Why you</dt>
          <dd>{AS_LABEL[item.as]}</dd>
        </dl>
        {item.note !== '' && <p className="text-warning od-approval-note">{item.note}</p>}
        {item.summary !== '' && (
          <p className="od-approval-summary" style={{ whiteSpace: 'pre-wrap' }}>
            {item.summary}
          </p>
        )}

        {rejecting && (
          <div className="form-group">
            <label className="control-label" htmlFor={`reject-reason-${item.id}`}>
              Why do you reject it? The requester reads this.
            </label>
            <textarea
              id={`reject-reason-${item.id}`}
              className="form-control"
              rows={3}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}

        <div className="aright">
          {rejecting ? (
            <>
              <button type="button" className="btn btn-default" onClick={() => setRejecting(false)}>
                Cancel
              </button>{' '}
              <button
                type="button"
                className="btn btn-danger od-approval-reject-confirm"
                disabled={reason.trim() === '' || decide.isPending}
                onClick={() => decide.mutate('reject')}
              >
                Reject
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-default od-approval-reject"
                disabled={decide.isPending}
                onClick={() => setRejecting(true)}
              >
                <i className="fas fa-times" aria-hidden="true"></i> Reject
              </button>{' '}
              <button
                type="button"
                className="btn btn-success od-approval-approve"
                disabled={decide.isPending}
                onClick={() => decide.mutate('approve')}
              >
                <i className="fas fa-check" aria-hidden="true"></i> Approve
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export function AwaitingApprovalPage() {
  const list = useQuery(myApprovalsQuery())

  if (list.error) return <LoadFailed what="Your approvals" error={list.error} onRetry={() => void list.refetch()} />
  if (!list.data) return <Spinner />

  return (
    <div className="od-awaiting-approval">
      <h3>Awaiting my approval</h3>
      {list.data.items.length === 0 ? (
        <p className="od-approval-empty" style={{ color: '#8d8d8d' }}>
          Nothing is waiting for your approval.
        </p>
      ) : (
        list.data.items.map((item) => <ApprovalCard key={item.id} item={item} />)
      )}
    </div>
  )
}
